import { Component, inject } from '@angular/core';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import { UwBadge } from '../../shared/ui/badge/uw-badge';
import { UwButton } from '../../shared/ui/button/uw-button';
import { UwProgressRing } from '../../shared/ui/progress-ring/uw-progress-ring';
import { UwProgress } from '../../shared/ui/progress/uw-progress';
import { UwAvatar } from '../../shared/ui/avatar/uw-avatar';
import { ToastService } from '../../core/services/toast.service';
import { ENRICHMENT_CARDS, RULE_ROWS, PORTFOLIO, ANALYTICS, COLLABORATION, COMPLIANCE } from '../../core/services/mock-data';
import { UwCompactMoneyPipe } from '../../shared/pipes/format.pipes';

const SOURCE_ICONS: Record<string, string> = {
  'Dun & Bradstreet': 'database',
  'Verisk / ISO': 'file-search',
  CoreLogic: 'map-pin',
  OSHA: 'shield-check',
  'NOAA / Geo hazard': 'globe',
  'Secretary of State (TX)': 'building-2',
};

@Component({
  selector: 'uw-classic-enrichment-page',
  standalone: true,
  imports: [UwIcon, UwBadge, UwButton],
  template: `
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div>
        <h2 class="typo-title text-primary">External data enrichment</h2>
        <p class="text-[11.5px] text-text-muted">Third-party sources matched to the account.</p>
      </div>
      <uw-button variant="secondary" size="sm" icon="refresh" (click)="rerun()">Re-run enrichment</uw-button>
    </div>
    <div class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
      @for (card of cards; track card.id) {
        <article class="rounded-xl border border-line bg-surface p-4 shadow-sm">
          <div class="flex items-start justify-between gap-2">
            <span class="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <uw-icon [name]="sourceIcon(card.source)" [size]="14" />
            </span>
            <uw-badge [tone]="card.tone" subtle>{{ card.match }}</uw-badge>
          </div>
          <h3 class="mt-3 text-[13px] font-semibold text-primary">{{ card.source }}</h3>
          <p class="text-[11px] font-medium text-ai">{{ card.type }}</p>
          <p class="mt-2 text-[11.5px] leading-snug text-secondary">{{ card.detail }}</p>
        </article>
      }
    </div>
  `,
})
export class ClassicEnrichmentPage {
  private readonly toast = inject(ToastService);

  protected readonly cards = ENRICHMENT_CARDS;

  protected sourceIcon(source: string): string {
    return SOURCE_ICONS[source] ?? 'database';
  }

  protected rerun(): void {
    this.toast.info('Enrichment re-ran', '6 sources refreshed — one new match to review.');
  }
}

@Component({
  selector: 'uw-classic-rules-page',
  standalone: true,
  imports: [UwBadge],
  template: `
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div>
        <h2 class="typo-title text-primary">Rules Engine results</h2>
        <p class="text-[11.5px] text-text-muted">Appetite, authority and regulatory gates for this submission.</p>
      </div>
      <div class="flex gap-1.5">
        <uw-badge tone="success" subtle>{{ passes() }} pass</uw-badge>
        <uw-badge tone="warning" subtle>{{ referrals() }} referral</uw-badge>
      </div>
    </div>
    <div class="mt-4 space-y-2">
      @for (row of rows; track row.id) {
        <article class="flex items-center gap-3 rounded-xl border border-line bg-surface p-3.5 shadow-sm">
          <uw-badge [tone]="row.tone" [icon]="resultIcon(row.result)">{{ row.result }}</uw-badge>
          <div class="min-w-0 flex-1">
            <p class="text-[12.5px] font-semibold text-primary">{{ row.name }}</p>
            <p class="truncate text-[11px] text-text-muted">{{ row.detail }}</p>
          </div>
          <uw-badge tone="neutral" subtle>{{ row.category }}</uw-badge>
        </article>
      }
    </div>
  `,
})
export class ClassicRulesPage {
  protected readonly rows = RULE_ROWS;

  protected passes(): number {
    return this.rows.filter((r) => r.result === 'Pass').length;
  }

  protected referrals(): number {
    return this.rows.filter((r) => r.result === 'Referral').length;
  }

  protected resultIcon(result: string): string {
    return result === 'Pass' ? 'check-circle' : result === 'Referral' ? 'alert-triangle' : 'x-circle';
  }
}

@Component({
  selector: 'uw-classic-portfolio-page',
  standalone: true,
  imports: [UwIcon, UwProgressRing, UwProgress, UwCompactMoneyPipe],
  template: `
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      @for (s of data.stats; track s.label) {
        <div class="rounded-xl border border-line bg-surface p-4 shadow-sm">
          <p class="typo-label">{{ s.label }}</p>
          <p class="mt-1 typo-mono text-[22px] font-bold text-primary">{{ s.value }}</p>
          <p class="mt-0.5 text-[11px]" [class]="s.dir === 'up' ? 'text-success' : 'text-warning'">
            <uw-icon [name]="s.dir === 'up' ? 'trending-up' : 'trending-down'" [size]="11" /> {{ s.delta }}
          </p>
        </div>
      }
    </div>

    <div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
      <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
        <h3 class="typo-title mb-3 text-primary">Geographic mix</h3>
        <div class="space-y-2.5">
          @for (g of data.geo; track g.label) {
            <div>
              <div class="mb-1 flex justify-between text-[11px]">
                <span class="text-secondary">{{ g.label }}</span>
                <span class="typo-mono text-primary">{{ g.pct }}%</span>
              </div>
              <uw-progress [value]="g.pct" tone="info" [label]="g.label" />
            </div>
          }
        </div>
      </section>
      <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
        <h3 class="typo-title mb-3 text-primary">Industry mix</h3>
        <div class="space-y-2.5">
          @for (i of data.industry; track i.label) {
            <div>
              <div class="mb-1 flex justify-between text-[11px]">
                <span class="text-secondary">{{ i.label }}</span>
                <span class="typo-mono text-primary">{{ i.pct }}%</span>
              </div>
              <uw-progress [value]="i.pct" tone="ai" [label]="i.label" />
            </div>
          }
        </div>
      </section>
      <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
        <h3 class="typo-title mb-3 text-primary">PML utilization</h3>
        <div class="flex flex-col items-center justify-center py-2">
          <uw-progress-ring [value]="data.pmlUtilization" [size]="120" [stroke]="10" tone="warning" [label]="'PML cap'" />
          <p class="mt-3 max-w-[24ch] text-center text-[11px] leading-snug text-text-muted">
            Share of aggregate catastrophe limit consumed by this submission's probable maximum loss.
          </p>
        </div>
      </section>
    </div>

    <section class="mt-4 overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
      <header class="border-b border-line px-4 py-3">
        <h3 class="typo-title text-primary">Top accounts by premium</h3>
      </header>
      <table class="w-full text-[12px]">
        <thead>
          <tr class="bg-surface-sunken/60">
            <th class="typo-label px-4 py-2.5 text-left">Account</th>
            <th class="typo-label px-4 py-2.5 text-left">Line</th>
            <th class="typo-label px-4 py-2.5 text-right">TIV</th>
            <th class="typo-label px-4 py-2.5 text-right">Premium</th>
            <th class="typo-label px-4 py-2.5 text-right">Loss ratio</th>
          </tr>
        </thead>
        <tbody>
          @for (a of data.topAccounts; track a.account) {
            <tr class="border-t border-line">
              <td class="px-4 py-2.5 font-medium text-primary">{{ a.account }}</td>
              <td class="px-4 py-2.5 text-secondary">{{ a.line }}</td>
              <td class="px-4 py-2.5 text-right typo-mono text-secondary">{{ a.tiv | uwCompactMoney }}</td>
              <td class="px-4 py-2.5 text-right typo-mono text-secondary">{{ a.premium | uwCompactMoney }}</td>
              <td class="px-4 py-2.5 text-right typo-mono font-semibold" [class]="a.lossRatio > 55 ? 'text-danger' : 'text-success'">
                {{ a.lossRatio }}%
              </td>
            </tr>
          }
        </tbody>
      </table>
    </section>
  `,
})
export class ClassicPortfolioPage {
  protected readonly data = PORTFOLIO;
}

@Component({
  selector: 'uw-classic-analytics-page',
  standalone: true,
  imports: [UwIcon, UwProgress, UwCompactMoneyPipe],
  template: `
    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      @for (s of data.stats; track s.label) {
        <div class="rounded-xl border border-line bg-surface p-4 shadow-sm">
          <p class="typo-label">{{ s.label }}</p>
          <p class="mt-1 typo-mono text-[22px] font-bold text-primary">{{ s.value }}</p>
          <p class="mt-0.5 text-[11px] text-success">
            <uw-icon name="trending-up" [size]="11" /> {{ s.delta }}
          </p>
        </div>
      }
    </div>

    <div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
        <h3 class="typo-title mb-3 text-primary">Hit ratio by segment</h3>
        <div class="space-y-2.5">
          @for (seg of data.segments; track seg.label) {
            <div>
              <div class="mb-1 flex justify-between text-[11px]">
                <span class="text-secondary">{{ seg.label }}</span>
                <span class="typo-mono text-primary">{{ seg.pct }}%</span>
              </div>
              <uw-progress [value]="seg.pct" tone="ai" [label]="seg.label" />
            </div>
          }
        </div>
      </section>
      <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
        <h3 class="typo-title mb-3 text-primary">Avg. cycle time (days)</h3>
        <div class="flex h-40 items-end gap-2">
          @for (c of data.cycleTime; track c.month) {
            <div class="flex flex-1 flex-col items-center gap-1">
              <span class="typo-mono text-[10px] text-secondary">{{ c.days }}</span>
              <div class="w-full rounded-t-md bg-accent/80" [style.height.px]="c.days * 18" [style.opacity]="cycleOpacity(c.days)"></div>
              <span class="text-[10px] text-text-muted">{{ c.month }}</span>
            </div>
          }
        </div>
      </section>
    </div>

    <section class="mt-4 overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
      <header class="border-b border-line px-4 py-3">
        <h3 class="typo-title text-primary">Underwriter scorecard</h3>
      </header>
      <table class="w-full text-[12px]">
        <thead>
          <tr class="bg-surface-sunken/60">
            <th class="typo-label px-4 py-2.5 text-left">Underwriter</th>
            <th class="typo-label px-4 py-2.5 text-right">Submissions</th>
            <th class="typo-label px-4 py-2.5 text-right">Hit ratio</th>
            <th class="typo-label px-4 py-2.5 text-right">Avg. premium</th>
            <th class="typo-label px-4 py-2.5 text-right">Loss ratio</th>
            <th class="typo-label px-4 py-2.5 text-right">Cycle time</th>
          </tr>
        </thead>
        <tbody>
          @for (row of data.scorecard; track row.uw) {
            <tr class="border-t border-line" [class]="row.uw === 'Team average' ? 'bg-surface-sunken/40 font-semibold' : ''">
              <td class="px-4 py-2.5 font-medium text-primary">{{ row.uw }}</td>
              <td class="px-4 py-2.5 text-right typo-mono text-secondary">{{ row.subs }}</td>
              <td class="px-4 py-2.5 text-right typo-mono text-secondary">{{ row.hitRatio }}%</td>
              <td class="px-4 py-2.5 text-right typo-mono text-secondary">{{ row.avgPremium | uwCompactMoney }}</td>
              <td class="px-4 py-2.5 text-right typo-mono font-semibold" [class]="row.lossRatio > 55 ? 'text-danger' : 'text-success'">{{ row.lossRatio }}%</td>
              <td class="px-4 py-2.5 text-right typo-mono text-secondary">{{ row.cycleTime }}</td>
            </tr>
          }
        </tbody>
      </table>
    </section>
  `,
})
export class ClassicAnalyticsPage {
  protected readonly data = ANALYTICS;

  protected cycleOpacity(days: number): number {
    return Math.max(0.5, 1 - (days - 5) / 3);
  }
}

@Component({
  selector: 'uw-classic-collaboration-page',
  standalone: true,
  imports: [UwIcon, UwAvatar, UwBadge],
  template: `
    <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <section class="rounded-xl border border-line bg-surface shadow-sm">
        <header class="border-b border-line px-4 py-3">
          <h3 class="typo-title flex items-center gap-1.5 text-primary"><uw-icon name="users" [size]="13" /> Internal team</h3>
        </header>
        <ul class="divide-y divide-line">
          @for (m of data.internal; track $index) {
            <li class="flex items-start gap-3 px-4 py-3">
              <uw-avatar [initials]="m.initials" [label]="m.name" size="sm" />
              <div class="min-w-0 flex-1">
                <p class="flex items-baseline gap-2 text-[11.5px]">
                  <span class="font-semibold text-primary">{{ m.name }}</span>
                  <span class="text-[10px] text-text-muted">{{ m.time }}</span>
                </p>
                <p class="mt-1 text-[12px] leading-relaxed text-secondary">{{ m.text }}</p>
              </div>
            </li>
          }
        </ul>
      </section>

      <section class="rounded-xl border border-line bg-surface shadow-sm">
        <header class="border-b border-line px-4 py-3">
          <h3 class="typo-title flex items-center gap-1.5 text-primary"><uw-icon name="chat" [size]="13" /> Broker thread</h3>
        </header>
        <ul class="divide-y divide-line">
          @for (m of data.broker; track $index) {
            <li class="flex items-start gap-3 px-4 py-3">
              <uw-avatar [initials]="m.initials" [label]="m.name" size="sm" />
              <div class="min-w-0 flex-1">
                <p class="flex items-baseline gap-2 text-[11.5px]">
                  <span class="font-semibold text-primary">{{ m.name }}</span>
                  <span class="text-[10px] text-text-muted">{{ m.time }}</span>
                </p>
                <p class="mt-1 text-[12px] leading-relaxed text-secondary">{{ m.text }}</p>
              </div>
            </li>
          }
        </ul>
      </section>
    </div>

    <section class="mt-4 overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
      <header class="border-b border-line px-4 py-3">
        <h3 class="typo-title text-primary">Open tasks</h3>
      </header>
      <ul class="divide-y divide-line">
        @for (t of data.tasks; track t.task) {
          <li class="flex items-center gap-3 px-4 py-3">
            <uw-badge [tone]="t.tone" subtle>{{ t.status }}</uw-badge>
            <span class="min-w-0 flex-1 truncate text-[12.5px] font-medium text-primary">{{ t.task }}</span>
            <span class="text-[11px] text-text-muted">{{ t.assigned }} · {{ t.due }}</span>
          </li>
        }
      </ul>
    </section>
  `,
})
export class ClassicCollaborationPage {
  protected readonly data = COLLABORATION;
}

@Component({
  selector: 'uw-classic-compliance-page',
  standalone: true,
  imports: [UwIcon, UwBadge],
  template: `
    <div class="grid grid-cols-1 gap-4 xl:grid-cols-[1.3fr_1fr]">
      <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
        <h3 class="typo-title mb-3 text-primary">Audit trail</h3>
        <ol class="relative space-y-4 before:absolute before:bottom-2 before:left-[7px] before:top-2 before:w-px before:bg-line">
          @for (step of data.trail; track step.title) {
            <li class="relative flex items-start gap-3">
              <span class="z-10 mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border-2" [class]="step.state === 'done' ? 'border-success bg-success' : 'border-accent bg-surface ring-4 ring-accent/20'"></span>
              <div class="pt-0">
                <p class="text-[12.5px] font-semibold text-primary">{{ step.title }}</p>
                <p class="mt-0.5 text-[11px] text-text-muted">{{ step.meta }}</p>
              </div>
            </li>
          }
        </ol>
      </section>

      <div class="space-y-4">
        <section class="rounded-xl border border-line bg-surface shadow-sm">
          <header class="border-b border-line px-4 py-3">
            <h3 class="typo-title text-primary">Compliance checklist</h3>
          </header>
          <ul class="divide-y divide-line">
            @for (item of data.checklist; track item.name) {
              <li class="flex items-center gap-3 px-4 py-3">
                <uw-icon [name]="item.mark === 'check' ? 'check-circle' : 'more-horizontal'" [size]="14" [class]="item.mark === 'check' ? 'text-success' : 'text-text-muted'" />
                <div class="min-w-0 flex-1">
                  <p class="text-[12px] font-medium text-primary">{{ item.name }}</p>
                  <p class="text-[10.5px] text-text-muted">{{ item.meta }}</p>
                </div>
                <uw-badge [tone]="item.tone" subtle>{{ item.status }}</uw-badge>
              </li>
            }
          </ul>
        </section>

        <section class="rounded-xl border border-line bg-surface shadow-sm">
          <header class="border-b border-line px-4 py-3">
            <h3 class="typo-title text-primary">Access log</h3>
          </header>
          <ul class="divide-y divide-line">
            @for (log of data.accessLog; track $index) {
              <li class="flex items-center justify-between px-4 py-2.5 text-[11.5px]">
                <span class="text-secondary">{{ log.who }}</span>
                <span class="typo-mono text-[10.5px] text-text-muted">{{ log.when }}</span>
              </li>
            }
          </ul>
        </section>
      </div>
    </div>
  `,
})
export class ClassicCompliancePage {
  protected readonly data = COMPLIANCE;
}
